// Layer 3 shared projection: one case-stage clock for the landing marker and
// the case-page stepper.

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { PipelineStageId } from '@/versions/v1/modules/advisory/PipelineStrip.transform';

export type CaseStageMilestones = {
  signalAt: SeriesTime | null;
  excursionAt: SeriesTime | null;
  raisedAt: SeriesTime | null;
  diagnosisAt: SeriesTime | null;
  actionAt: SeriesTime | null;
  valueAt: SeriesTime | null;
  learnAt: SeriesTime | null;
};

export type CaseStageTimeline = ReadonlyMap<string, CaseStageMilestones>;

export function caseStageAt(cursor: SeriesTime, timeline: CaseStageMilestones): PipelineStageId | null {
  if (!timeline.raisedAt || timeline.raisedAt > cursor) return null;
  let stage: PipelineStageId = 'cases';
  if (timeline.diagnosisAt && timeline.diagnosisAt <= cursor) stage = 'diagnosis';
  if (timeline.actionAt && timeline.actionAt <= cursor) stage = 'action';
  const valueCaptured = timeline.valueAt !== null && timeline.valueAt <= cursor;
  const learningRecorded = timeline.learnAt !== null && timeline.learnAt <= cursor;
  if (valueCaptured) stage = 'value';
  // Approval creates the learning artefact immediately, but the case stays in
  // Value tracking until its linked share is captured. Learn is the next case
  // stage only when both milestones have arrived.
  if (learningRecorded) stage = 'value';
  if (valueCaptured && learningRecorded) stage = 'learn';
  return stage;
}

/** Signal and excursion milestones place a fixture marker before its case is raised. */
export function caseMarkerStageAt(
  cursor: SeriesTime,
  timeline: CaseStageMilestones | undefined,
): PipelineStageId | null {
  if (!timeline) return null;
  if (timeline.raisedAt && cursor > timeline.raisedAt) return caseStageAt(cursor, timeline);
  if (timeline.excursionAt && cursor >= timeline.excursionAt) return 'excursions';
  if (timeline.signalAt && cursor >= timeline.signalAt) return 'signals';
  if (timeline.raisedAt && cursor >= timeline.raisedAt) return 'cases';
  return null;
}

/** Compatibility for existing replay probes. */
export const zincMarkerStageAt = caseMarkerStageAt;
